// src/pages/PublicTracking.js

import React, { useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaBoxOpen, FaWarehouse, FaTruckLoading, FaRoute, FaCalendarTimes,
  FaCheckCircle, FaWhatsapp, FaBox, FaShippingFast, FaMapMarkerAlt,
  FaUser, FaPhone, FaBuilding, FaCalendarDay, FaClipboardList, FaClock,
  FaTruck, FaRoad, FaHandshake, FaHome, FaRegCalendarCheck // <-- ¡Añadido FaRegCalendarCheck aquí!
} from 'react-icons/fa';
import { GiCardboardBox } from 'react-icons/gi';

function PublicTracking() {
  const [orderNumber, setOrderNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Mapeo de estados a iconos y mensajes con colores mejorados
  const statusMap = {
    'En proceso de Compra': {
      icon: <FaBoxOpen />,
      message: "¡Tu pedido está siendo procesado! Estamos preparando todo para su envío.",
      color: '#FFC107', // Amarillo cálido
      stageMessage: "Su pedido está en proceso de compra."
    },
    'En almacén': {
      icon: <FaWarehouse />,
      message: "¡Tu pedido ha llegado a nuestro almacén! Pronto estará listo para su siguiente etapa.",
      color: '#17A2B8', // Azul cian
      stageMessage: "Su pedido se encuentra en almacén."
    },
    'Programado para despacho': {
      icon: <FaTruckLoading />,
      message: "¡Tu pedido está programado para despacho! Se asignará un transporte pronto.",
      color: '#6F42C1', // Púrpura vibrante
      stageMessage: "Su pedido se encuentra programado para entrega."
    },
    'En Ruta': {
      icon: <FaRoute />,
      message: "¡Tu pedido está en camino! Un conductor lo llevará directamente a tu destino.",
      color: '#28A745', // Verde esmeralda
      stageMessage: "Su pedido está en ruta, esté atento a la llamada de nuestro conductor."
    },
    'Reprogramado': {
      icon: <FaCalendarTimes />,
      message: "Tu pedido ha sido reprogramado. Por favor, contáctanos para más detalles.",
      color: '#DC3545', // Rojo intenso
      stageMessage: "Su pedido ha sido reprogramado."
    },
    'Entregado Exitosamente': {
      icon: <FaCheckCircle />,
      message: "¡Tu pedido ha sido entregado exitosamente! Gracias por confiar en nosotros.",
      color: '#20C997', // Verde azulado brillante
      stageMessage: "Su pedido se entregó con éxito, ¡Gracias por confiar en Implementos y Reactivos!"
    },
    'Pedido listo para Recojo': {
        icon: <GiCardboardBox />,
        message: "¡Tu pedido está listo para recojo en nuestro almacén! Te esperamos.",
        color: '#FD7E14', // Naranja vibrante
        stageMessage: "Su pedido está listo para recojo en almacén."
    },
    'Entregado en Agencia': {
        icon: <FaBox />,
        message: "Tu pedido ha sido entregado en la agencia de transporte. Puedes recogerlo en el destino.",
        color: '#007BFF', // Azul brillante
        stageMessage: "Su pedido se encuentra en agencia."
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setOrder(null);
    setError('');
    setLoading(true);

    if (!/^\d{5}$/.test(orderNumber)) {
      setError('El número de pedido debe ser de 5 dígitos numéricos.');
      setLoading(false);
      return;
    }

    try {
      const orderDocRef = doc(db, 'pedidos', orderNumber);
      const orderDocSnap = await getDoc(orderDocRef);

      if (orderDocSnap.exists()) {
        const fetchedOrder = { id: orderDocSnap.id, ...orderDocSnap.data() };
        setOrder(fetchedOrder);
      } else {
        setError('Pedido no encontrado. Por favor, verifica el número.');
      }
    } catch (err) {
      console.error("Error al buscar pedido:", err.message);
      setError("Ocurrió un error al buscar el pedido. Intenta de nuevo más tarde.");
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear la fecha y hora
  const formatTimestamp = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return isoString;
    }
  };

  // Variantes para animaciones de Framer Motion
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const stageIconVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: { scale: 1, opacity: 1, transition: { type: "spring", stiffness: 200, damping: 10 } },
    hover: { scale: 1.1 }
  };

  // Lógica para construir las etapas a mostrar dinámicamente
  const getDynamicStages = (order) => {
    if (!order || !order.statusHistory) return [];

    const sortedHistory = [...order.statusHistory].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const processedStages = [];
    const seenCanonicalStatuses = new Set(); // Para asegurar que cada estado canónico se añada solo una vez

    // 1. Determinar la ruta de entrega principal del pedido de forma estricta
    let effectivePathType = 'delivery'; // Valor por defecto: Envío Local

    // Prioridad 1: Si el estado actual es "Pedido listo para Recojo", es una ruta de recojo
    if (order.currentStatus === 'Pedido listo para Recojo') {
        effectivePathType = 'pickup';
    }
    // Prioridad 2: Si el estado actual es "Entregado en Agencia", es una ruta de envío a provincia
    else if (order.currentStatus === 'Entregado en Agencia') {
        effectivePathType = 'province_delivery';
    }
    // Prioridad 3: Si el estado actual es "Programado para despacho" o "En Ruta"
    else if (order.currentStatus === 'Programado para despacho' || order.currentStatus === 'En Ruta') {
        if (order.dispatchType === 'Provincia') {
            effectivePathType = 'province_delivery';
        } else {
            effectivePathType = 'delivery';
        }
    }
    // Prioridad 4: Si el estado actual es "Entregado Exitosamente"
    else if (order.currentStatus === 'Entregado Exitosamente') {
        // Intentar inferir la ruta de entrega basada en el dispatchType o el historial previo
        if (order.dispatchType === 'Provincia') {
            effectivePathType = 'province_delivery';
        } else {
            // Buscar el último estado significativo antes de la entrega exitosa
            const lastNonReprogrammedStatus = sortedHistory.slice().reverse().find(entry =>
                entry.status !== 'Entregado Exitosamente' && entry.status !== 'Reprogramado'
            );
            if (lastNonReprogrammedStatus && lastNonReprogrammedStatus.status === 'Pedido listo para Recojo') {
                effectivePathType = 'pickup';
            } else {
                effectivePathType = 'delivery'; // Por defecto a local si no se puede determinar claramente
            }
        }
    }
    // Prioridad 5: Para estados iniciales como 'En proceso de Compra' o 'En almacén'
    // La ruta se determina por el dispatchType o por defecto a 'delivery'
    else if (order.dispatchType === 'Provincia') {
        effectivePathType = 'province_delivery';
    }


    // 2. Definir las secuencias canónicas (esperadas) para cada ruta
    const canonicalPaths = {
        'delivery': ['En proceso de Compra', 'En almacén', 'Programado para despacho', 'En Ruta', 'Entregado Exitosamente'],
        'province_delivery': ['En proceso de Compra', 'En almacén', 'Programado para despacho', 'En Ruta', 'Entregado en Agencia', 'Entregado Exitosamente'],
        'pickup': ['En proceso de Compra', 'En almacén', 'Pedido listo para Recojo', 'Entregado Exitosamente']
    };

    const currentCanonicalPath = canonicalPaths[effectivePathType];

    // 3. Construir la secuencia de etapas a mostrar, adhiriéndose estrictamente a la ruta determinada
    const tempStages = []; // Usamos un array temporal para construir y luego ordenar

    for (const entry of sortedHistory) {
        const status = entry.status;

        // Manejar 'Reprogramado' por separado: siempre añadir si ocurre y no es consecutivo
        if (status === 'Reprogramado') {
            const lastAddedStage = tempStages[tempStages.length - 1];
            // Añadir si es la primera etapa o si la última etapa añadida no era también 'Reprogramado'
            if (!lastAddedStage || lastAddedStage.status !== 'Reprogramado') {
                tempStages.push({ status: status, historyEntry: entry });
            }
            continue; // Saltar el resto del bucle para 'Reprogramado'
        }

        // Solo añadir estados canónicos si son parte de la ruta determinada
        // Y no han sido añadidos aún como una etapa principal.
        // Esto es donde ocurre el filtrado estricto basado en la ruta determinada.
        if (currentCanonicalPath.includes(status) && !seenCanonicalStatuses.has(status)) {
            tempStages.push({ status: status, historyEntry: entry });
            seenCanonicalStatuses.add(status);
        }
    }

    // 4. Ordenar las etapas finales para asegurar la cronología
    // Este ordenamiento es crucial porque 'Reprogramado' pudo haber sido insertado fuera de orden
    processedStages.push(...tempStages.sort((a, b) => new Date(a.historyEntry.timestamp) - new Date(b.historyEntry.timestamp)));

    return processedStages;
  };

  const dynamicStages = order ? getDynamicStages(order) : [];

  return (
    <div className="public-tracking-page-wrapper">
      <div className="tracking-search-box">
        <h1 className="login-title">Seguimiento de Pedidos</h1>
        
        {/* Aquí se inserta el logo */}
        <img 
          src="/IR.png" // Ruta a tu archivo de logo en la carpeta public
          alt="Logo de la Empresa" 
          style={logoStyle} 
        />

        <p className="login-subtitle">Implementos y Reactivos EIRL</p>
        <p style={{ fontSize: '1em', color: '#666', marginBottom: '25px' }}>
          "Tu tranquilidad es nuestra prioridad. Sigue el progreso de tu pedido en tiempo real."
        </p>

        <form onSubmit={handleSearch} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <div className="login-form-group">
            <label htmlFor="orderNumberSearch" style={{ color: 'var(--text-dark)' }}>Ingresa tu Número de Pedido:</label>
            <input
              type="text"
              id="orderNumberSearch"
              placeholder="Ej: 12345"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              maxLength={5}
              pattern="\d{5}"
              title="Debe ser un número de 5 dígitos"
              required
            />
          </div>
          <motion.button
            type="submit"
            className="login-button"
            disabled={loading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {loading ? 'Buscando...' : 'Buscar Pedido'}
          </motion.button>
        </form>

        {error && <p className="login-error" style={{ marginTop: '20px' }}>{error}</p>}
      </div>

      <AnimatePresence>
        {!order && ( // Mostrar la guía rápida solo si no hay resultados de pedido
          <motion.div
            key="guide-box"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="tracking-guide-box"
          >
            <h4 style={{ color: 'var(--secondary-blue)', marginTop: '0' }}>Guía Rápida:</h4>
            <p>1. Ingresa el número de pedido de 5 dígitos que te proporcionamos.</p>
            <p>2. Haz clic en "Buscar Pedido".</p>
            <p>3. ¡Listo! Verás el estado actual y los detalles de tu envío.</p>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {order && (
          <motion.div
            key="results-container"
            className="tracking-results-container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, y: 20, transition: { duration: 0.3 } }}
            style={{ border: `2px solid ${statusMap[order.currentStatus]?.color || '#ccc'}` }}
          >
            <motion.h3 variants={itemVariants} style={{ color: 'var(--secondary-blue)', marginBottom: '15px', textAlign: 'center' }}>
              Detalles del Pedido #{order.orderNumber}
            </motion.h3>
            <motion.div variants={itemVariants} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
              {React.cloneElement(statusMap[order.currentStatus]?.icon, { className: "status-icon", style: { color: statusMap[order.currentStatus]?.color } })}
              <motion.h4 variants={itemVariants} style={{ color: statusMap[order.currentStatus]?.color || 'var(--text-dark)', fontSize: '1.8em', margin: '10px 0' }}>
                {order.currentStatus}
              </motion.h4>
              <motion.p variants={itemVariants} style={{ color: '#555', fontSize: '1.1em', textAlign: 'center', maxWidth: '80%' }}>
                {statusMap[order.currentStatus]?.stageMessage || "Estado desconocido."}
              </motion.p>
            </motion.div>

            {/* Nueva visualización de etapas dinámicas e individuales */}
            <motion.div variants={itemVariants} className="tracking-stages-container">
                {dynamicStages.map((stage, index) => (
                    <React.Fragment key={stage.historyEntry.timestamp + stage.status}> {/* Usar timestamp para key única */}
                        <div className="tracking-stage">
                            <motion.div
                                className={`stage-icon-wrapper ${order.currentStatus === stage.status ? 'pulsating-icon' : ''}`}
                                variants={stageIconVariants}
                                whileHover="hover"
                                style={{ '--pulse-base-color': statusMap[stage.status]?.color || 'var(--secondary-blue)' }}
                            >
                                {/* Usar el icono específico para cada estado */}
                                {React.cloneElement(statusMap[stage.status]?.icon, { className: "stage-icon" })}
                            </motion.div>
                            <h5 className="stage-title" style={{ color: statusMap[stage.status]?.color || 'var(--secondary-blue)' }}>{stage.status}</h5>
                            <div className="stage-status-list">
                                {/* Mostrar solo la entrada de historial que corresponde a este estado */}
                                <p className={`stage-status-item ${stage.status === order.currentStatus ? 'current-stage-status' : ''}`}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <FaCheckCircle className="status-check-icon" /> {stage.status}
                                    </span>
                                    <span className="status-date">{formatTimestamp(stage.historyEntry.timestamp)}</span>
                                    <span className="status-message">{statusMap[stage.status]?.stageMessage}</span>
                                </p>
                            </div>
                        </div>
                        {/* Agregar separador solo si no es la última etapa visible */}
                        {index < dynamicStages.length - 1 && <div className="stage-separator"></div>}
                    </React.Fragment>
                ))}
            </motion.div>

            {order.currentStatus === 'Reprogramado' && (
                <motion.div variants={itemVariants} className="tracking-section-card reprogrammed-card">
                    <h4 className="tracking-section-title"><FaCalendarTimes className="icon-color-red" /> Pedido Reprogramado</h4>
                    <p style={{ color: '#DC3545', fontWeight: 'bold' }}>
                        Su pedido ha sido reprogramado. Por favor, revise los detalles en el historial o contáctenos para más información.
                    </p>
                    {order.observations && <p><strong>Observaciones:</strong> {order.observations}</p>}
                </motion.div>
            )}

            {/* Datos del Cliente */}
            <motion.div variants={itemVariants} className="tracking-section-card">
              <h4 className="tracking-section-title">Datos del Cliente</h4>
              <div className="tracking-grid">
                <p><FaBuilding className="icon-color-blue" /> <strong>Empresa:</strong> {order.clientName}</p>
                <p><FaCalendarDay className="icon-color-blue" /> <strong>Fecha de Pedido:</strong> {order.orderDate}</p>
                <p><FaRegCalendarCheck className="icon-color-blue" /> <strong>Fecha Prevista de Entrega:</strong> {order.expectedDeliveryDate}</p>
                <p><FaMapMarkerAlt className="icon-color-blue" /> <strong>Dirección de Entrega:</strong> {order.deliveryAddress}</p>
                <p><FaUser className="icon-color-blue" /> <strong>Contacto:</strong> {order.contactName}</p>
              </div>
            </motion.div>

            {/* Datos del Transporte (condicional) */}
            {(order.currentStatus === 'Programado para despacho' || order.currentStatus === 'En Ruta') && (
              <motion.div variants={itemVariants} className="tracking-section-card">
                <h4 className="tracking-section-title">Datos del Transporte</h4>
                <div className="tracking-grid">
                  <p><FaShippingFast className="icon-color-blue" /> <strong>Tipo de Despacho:</strong> {order.dispatchType || 'N/A'}</p>
                  {order.dispatchDeliveryAddress && <p><FaMapMarkerAlt className="icon-color-blue" /> <strong>Dirección de Despacho:</strong> {order.dispatchDeliveryAddress}</p>}
                  {order.driverName && <p><FaUser className="icon-color-blue" /> <strong>Conductor:</strong> {order.driverName}</p>}
                  {order.driverPhone && <p><FaPhone className="icon-color-blue" /> <strong>Teléfono:</strong> {order.driverPhone}</p>}
                  {order.vehiclePlate && <p><FaTruck className="icon-color-blue" /> <strong>Placa:</strong> {order.vehiclePlate} ({order.vehicleModel || 'N/A'})</p>}
                </div>
              </motion.div>
            )}

            {order.currentStatus === 'Entregado en Agencia' && (
              <motion.div variants={itemVariants} className="tracking-section-card">
                <h4 className="tracking-section-title">Datos de Agencia</h4>
                <div className="tracking-grid">
                  <p><FaClipboardList className="icon-color-blue" /> <strong>Tracking Agencia:</strong> {order.agencyTrackingNumber}</p>
                  <p><FaBuilding className="icon-color-blue" /> <strong>Agencia:</strong> {order.agencyName}</p>
                  <p><FaMapMarkerAlt className="icon-color-blue" /> <strong>Destino Agencia:</strong> {order.agencyDestination}</p>
                  <p><FaBoxOpen className="icon-color-blue" /> <strong>Clave Recojo:</strong> {order.agencyKey}</p>
                </div>
              </motion.div>
            )}

            {order.observations && order.currentStatus !== 'Reprogramado' && (
              <motion.div variants={itemVariants} className="tracking-section-card">
                <h4 className="tracking-section-title">Observaciones Adicionales</h4>
                <p>{order.observations}</p>
              </motion.div>
            )}

            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <a
                href={`https://wa.me/51996655941?text=Hola%2C%20estoy%20consultando%20sobre%20mi%20pedido%20%23${order.orderNumber}.%0AEmpresa%3A%20${encodeURIComponent(order.clientName)}.%0AEstado%3A%20${encodeURIComponent(order.currentStatus)}.`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 25px',
                  backgroundColor: '#25D366',
                  color: 'white',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '1.1em',
                  fontWeight: 'bold',
                  transition: 'background-color 0.3s ease, transform 0.2s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <FaWhatsapp style={{ fontSize: '1.5em' }} /> Consultar Pedido por WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Estilos (mantén tus estilos CSS si los tienes, estos son solo de ejemplo)
const containerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start', // Alinea al inicio para que el contenido no esté centrado verticalmente
  minHeight: '100vh',
  backgroundColor: 'var(--primary-blue)', // Color de fondo de tu app
  padding: '40px 20px',
  boxSizing: 'border-box',
};

const cardStyle = {
  backgroundColor: 'white',
  padding: '40px',
  borderRadius: '15px',
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
  textAlign: 'center',
  maxWidth: '500px',
  width: '100%',
};

const titleStyle = {
  color: 'var(--primary-red)', // Color de tu título principal
  fontSize: '2.5em',
  marginBottom: '10px',
};

// DEFINICIÓN DE logoStyle AÑADIDA AQUÍ
const logoStyle = {
  maxWidth: '150px', // Ajusta el tamaño del logo según sea necesario
  height: 'auto',
  margin: '20px auto', // Margen superior e inferior para separarlo
  display: 'block', // Para centrarlo con margin: auto
};

const subtitleStyle = {
  color: 'var(--secondary-blue)', // Color de tu subtítulo
  fontSize: '1.8em',
  marginTop: '10px',
  marginBottom: '20px',
};

const taglineStyle = {
  color: '#666',
  fontSize: '1.1em',
  marginBottom: '30px',
  fontStyle: 'italic',
};

const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  marginBottom: '20px',
};

const labelStyle = {
  fontSize: '1.1em',
  fontWeight: 'bold',
  color: 'var(--text-dark)',
  marginBottom: '5px',
  textAlign: 'left',
};

const inputStyle = {
  padding: '12px 15px',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  fontSize: '1em',
  width: 'calc(100% - 30px)', // Para compensar el padding
  boxSizing: 'border-box',
};

const buttonStyle = {
  backgroundColor: 'var(--primary-red)', // Color de tu botón principal
  color: 'white',
  padding: '12px 25px',
  borderRadius: '8px',
  border: 'none',
  fontSize: '1.1em',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease, transform 0.2s ease',
};

const errorStyle = {
  color: 'red',
  marginTop: '15px',
  fontWeight: 'bold',
};

const resultsContainerStyle = {
  marginTop: '30px',
  borderTop: '1px solid #eee',
  paddingTop: '20px',
  textAlign: 'left',
};

const resultsTitleStyle = {
  color: 'var(--secondary-blue)',
  fontSize: '1.6em',
  marginBottom: '15px',
  textAlign: 'center',
};

const detailStyle = {
  marginBottom: '10px',
  fontSize: '1.05em',
  color: '#333',
};

const transportDetailsStyle = {
  marginTop: '20px',
  padding: '15px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  backgroundColor: 'var(--light-blue)',
};

const historyTitleStyle = {
  color: 'var(--secondary-blue)',
  fontSize: '1.4em',
  marginTop: '25px',
  marginBottom: '10px',
};

const historyListStyle = {
  listStyleType: 'none',
  padding: 0,
};

const historyItemStyle = {
  backgroundColor: '#f9f9f9',
  padding: '10px',
  borderRadius: '5px',
  marginBottom: '8px',
  borderLeft: '4px solid var(--primary-blue)',
  fontSize: '0.95em',
  color: '#555',
};

export default PublicTracking;